import { BarChart3, Eye, History, MapPin, Truck } from "lucide-react";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { MyFortAgentItem } from "@/api/types";
import { C } from "@/components/modal/home";
import { DAY_RAT_MAP, getAgentTierKey } from "@/config/common";
import { showError, showInfo } from "@/lib/toast";
import { useMyAgentsUiStore } from "@/store/useMyAgentsUiStore";
import { formatEtherWithDecimals, formatThousands } from "@/lib/utils";
import { AGENT_IMAGE_URL_ACTIVE, calcAgentProgressPct } from "./constants";

export type MyAgentActivatedCardV2Props = {
  agent: MyFortAgentItem;
  onAddress: (agent: MyFortAgentItem) => void;
  onDeliveryStatus: (agent: MyFortAgentItem) => void;
  onIncomeHistory: (agent: MyFortAgentItem) => void;
};

export function MyAgentActivatedCardV2({
  agent,
  onAddress,
  onDeliveryStatus,
  onIncomeHistory,
}: MyAgentActivatedCardV2Props) {
  const { t } = useTranslation();
  const { openActiveDetail } = useMyAgentsUiStore();
  const tierLabel = t(getAgentTierKey(agent.name));
  const tagColor = C.amber;
  const todayEarnedU = formatThousands(
    formatEtherWithDecimals(agent.todayStaticPayoutUsdt ?? "0", 2),
    2
  );
  const rateWanFen = (agent.todayStaticPayoutRateBpms ?? 0) / 10000;
  const dailyRate = rateWanFen > 0 ? `${rateWanFen}%` : "-";
  const progressPct = calcAgentProgressPct(agent);
  const earnedDisplay = formatThousands(
    formatEtherWithDecimals(agent.staticPaidUsdt, 0),
    0
  );
  const capDisplay = formatThousands(
    formatEtherWithDecimals(agent.staticCapUsdt, 0),
    0
  );

  const showFeatureNotAvailable = () =>
    showError(t("toast.common.featureNotAvailable"));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${tagColor}04 0%, ${C.card} 40%, ${C.card2} 100%)`,
        border: `1px solid ${tagColor}15`,
      }}
    >
      <div className="p-4 pb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-14 h-14 rounded-xl overflow-hidden shrink-0"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${tagColor}10`,
            }}
          >
            <img
              src={AGENT_IMAGE_URL_ACTIVE}
              alt=""
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold" style={{ color: C.text }}>
                {agent.name}
              </span>
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                style={{
                  background: `${tagColor}15`,
                  color: tagColor,
                  border: `1px solid ${tagColor}20`,
                }}
              >
                {tierLabel}
              </span>
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: C.dim }}>
              {t("homePage.myAgentCard.activatedAt", {
                date: dayjs(agent.activatedAt).format("YYYY-MM-DD"),
              })}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                className="text-[10px] mono-num font-bold"
                style={{ color: C.green }}
              >
                {t("myAgentsPage.todayEarning")}+{todayEarnedU} U
              </span>
              <span className="text-[9px]" style={{ color: C.dim }}>
                {/* {t("myAgentsPage.dailyRate")} {dailyRate} */}
                {t("myAgentsPage.dailyRate")} {DAY_RAT_MAP[agent.name]}
              </span>
            </div>
          </div>
          <span
            className="text-[10px] px-2.5 py-1 rounded-full font-bold shrink-0"
            style={{
              background: `${C.primary}15`,
              color: C.primary,
              border: `1px solid ${C.primary}20`,
            }}
          >
            ● {t("homePage.myAgentCard.statusRunning")}
          </span>
        </div>

        <div className="mt-3">
          <div className="flex justify-between text-[9px] mb-1">
            <span style={{ color: C.dim }}>
              {t("homePage.myAgentActiveDetailDialog.earningsProgress")}
            </span>
            <span className="mono-num" style={{ color: C.muted }}>
              ${earnedDisplay} / ${capDisplay}
            </span>
          </div>
          <div
            className="h-1 rounded-full overflow-hidden"
            style={{ background: "rgba(255,255,255,0.04)" }}
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${progressPct}%`,
                background: `linear-gradient(90deg, ${C.primary}, ${C.cyan})`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="px-4 pb-3">
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all active:scale-95"
            style={{
              background: `${C.primary}06`,
              border: `1px solid ${C.primary}08`,
            }}
            onClick={() => onAddress(agent)}
          >
            <MapPin size={14} style={{ color: C.primary }} />
            <span
              className="text-[9px] font-medium"
              style={{ color: C.primary }}
            >
              {t("homePage.myAgentCard.deliveryAddress")}
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all active:scale-95"
            style={{
              background: `${C.cyan}06`,
              border: `1px solid ${C.cyan}08`,
            }}
            onClick={() => onDeliveryStatus(agent)}
          >
            <Truck size={14} style={{ color: C.cyan }} />
            <span className="text-[9px] font-medium" style={{ color: C.cyan }}>
              {t("homePage.myAgentCard.deliveryStatus")}
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all active:scale-95"
            style={{
              background: `${C.muted}06`,
              border: `1px solid rgba(136,146,168,0.12)`,
            }}
            onClick={() => openActiveDetail(agent.agentId)}
          >
            <Eye size={14} style={{ color: C.muted }} />
            <span className="text-[9px] font-medium" style={{ color: C.muted }}>
              {t("homePage.myAgentCard.viewDetail")}
            </span>
          </button>
        </div>
      </div>

      <div className="px-4 pb-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          className="py-2 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
          style={{
            background: `${C.green}10`,
            color: C.green,
            border: `1px solid ${C.green}15`,
          }}
          onClick={() => onIncomeHistory(agent)}
        >
          <History size={12} />
          {t("homePage.myAgentCard.incomeHistory")}
        </button>
        <button
          type="button"
          className="py-2 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
          style={{
            background: `${C.cyan}10`,
            color: C.cyan,
            border: `1px solid ${C.cyan}15`,
          }}
          onClick={showFeatureNotAvailable}
        >
          <BarChart3 size={12} />
          {t("homePage.myAgentHomeActive.enterAnalysis")}
        </button>
      </div>
    </motion.div>
  );
}
